import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import uiConfig from "../../config/uiConfig";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Checkbox,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

function TicketTable({
  tickets,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(
    tickets.length / rowsPerPage
  );

  const startIndex = (page - 1) * rowsPerPage;

  const paginatedTickets = tickets.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const allSelected = tickets.length > 0 && selectedIds.length === tickets.length;

  const isSelected = (ticketId) => selectedIds.includes(ticketId);

  const toggleSelectAll = (checked) => {
    onSelectionChange?.(checked ? tickets.map((ticket) => ticket.ticket_id) : []);
  };

  const toggleSelectOne = (ticketId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, ticketId]);
      return;
    }

    onSelectionChange?.(selectedIds.filter((id) => id !== ticketId));
  };

  const priorityStyle = (priority) => {
    switch (priority) {
      case "Critical":
        return {
          background: "#fee2e2",
          color: "#dc2626"
        };
      case "High":
        return {
          background: "#ffedd5",
          color: "#ea580c"
        };
      case "Medium":
        return {
          background: "#fef3c7",
          color: "#ca8a04"
        };
      case "Low":
        return {
          background: "#dcfce7",
          color: "#16a34a"
        };
      default:
        return {};
    }
  };

  const statusStyle = (status) => {
    switch (status) {
      case "Open":
        return {
          background: "#fef3c7",
          color: "#d97706"
        };
      case "Assigned":
        return {
          background: "#dbeafe",
          color: "#2563eb"
        };
      case "In Progress":
        return {
          background: "#ede9fe",
          color: "#7c3aed"
        };
      case "Resolved":
        return {
          background: "#dcfce7",
          color: "#16a34a"
        };
      case "Closed":
        return {
          background: "#e5e7eb",
          color: "#475569"
        };
      default:
        return {};
    }
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 15px rgba(0,0,0,0.05)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: "#f8fafc" }}>
              <TableCell>
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                />
              </TableCell>

              {[
                "Ticket",
                "Category",
                "Priority",
                "Status",
                "Assigned To",
                "Project",
                "Actions"
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#334155"
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={`ticket-skeleton-${index}`}>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width={160} height={22} />
                  <Skeleton variant="rounded" width={56} height={22} sx={{ mt: 0.6 }} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.6 }}>
                    <Skeleton variant="circular" width={22} height={22} />
                    <Skeleton variant="text" width={80} height={18} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={84} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={72} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={88} height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton variant="text" width={100} height={24} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={92} height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <Skeleton variant="circular" width={34} height={34} />
                    <Skeleton variant="circular" width={34} height={34} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedTickets.map((ticket) => (
              <TableRow
                key={ticket.ticket_id}
                hover
                sx={{
                  "&:hover": {
                    background: "#f8fafc"
                  }
                }}
              >
                <TableCell>
                  <Checkbox
                    checked={isSelected(ticket.ticket_id)}
                    onChange={(event) => toggleSelectOne(ticket.ticket_id, event.target.checked)}
                  />
                </TableCell>

                <TableCell>
                 

                  <Box
                    onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                    sx={{
                     color: uiConfig.buttonColor,
                     fontWeight: 600,
                      cursor: "pointer",
                      mt: 0.6,
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {ticket.ticket_id}
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.6 }}>
              
                    <Typography fontSize={12} color="#64748b">
                      {ticket.created_by}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip
                    label={ticket.category}
                    size="small"
                    sx={{
                      background: "#f1f5f9",
                      fontWeight: 600
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Chip
                    label={ticket.priority}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      ...priorityStyle(ticket.priority)
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Chip
                    label={ticket.status}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      ...statusStyle(ticket.status)
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                  
                    <Typography fontSize={14}>
                      {ticket.assigned_name || "Unassigned"}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip
                    label={ticket.project_name || "No Project"}
                    size="small"
                    sx={{
                      background: "#ecfeff",
                      color: "#155e75",
                      fontWeight: 600
                    }}
                  />
                </TableCell>

                <TableCell>
            
                  <IconButton
                    onClick={() => onEdit(ticket)}
                    sx={{
                      background: "#eff6ff",
                      mr: 1
                    }}
                  >
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>

                  <IconButton
                    onClick={() => onDelete(ticket)}
                    sx={{
                      background: "#fee2e2"
                    }}
                  >
                    <DeleteIcon
                      fontSize="small"
                      sx={{
                        color: "#dc2626"
                      }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedTickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 6 }}>
                  <Box
                    sx={{
                      maxWidth: 540,
                      mx: "auto",
                      textAlign: "center",
                      borderRadius: 3,
                      border: "1px solid #dbeafe",
                      background: "linear-gradient(145deg, #f8fbff 0%, #eef6ff 100%)",
                      p: 4
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: 120,
                        height: 90,
                        mx: "auto",
                        mb: 2
                      }}
                    >
                      <Box
                        component="svg"
                        viewBox="0 0 160 120"
                        sx={{ width: "100%", height: "100%" }}
                      >
                        <rect x="20" y="18" width="120" height="84" rx="12" fill="#dbeafe" />
                        <rect x="34" y="32" width="92" height="10" rx="5" fill="#bfdbfe" />
                        <rect x="34" y="50" width="70" height="10" rx="5" fill="#bfdbfe" />
                        <rect x="34" y="68" width="56" height="10" rx="5" fill="#bfdbfe" />
                      </Box>

                      <Box
                        sx={{
                          position: "absolute",
                          right: -8,
                          bottom: -8,
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: uiConfig.buttonColor,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 8px 16px rgba(37,99,235,0.35)"
                        }}
                      >
                        <SearchOffRoundedIcon sx={{ fontSize: 20 }} />
                      </Box>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1e3a8a",
                        fontWeight: 700,
                        mb: 0.8
                      }}
                    >
                      No tickets match your view
                    </Typography>

                    <Typography
                      sx={{
                        color: "#475569",
                        fontSize: 14,
                        mb: 1.8
                      }}
                    >
                      Try changing filters or create a new ticket to get started.
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.4,
                        py: 0.6,
                        borderRadius: 999,
                        background: "#dbeafe",
                        color: "#1d4ed8",
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
                      Clean slate ready
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Typography fontSize={14} color="#64748b">
            Showing {tickets.length === 0 ? 0 : startIndex + 1}
            {" "}
            to
            {" "}
            {Math.min(startIndex + rowsPerPage, tickets.length)}
            {" "}
            of {tickets.length} results
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={5}>5 / page</MenuItem>
              <MenuItem value={10}>10 / page</MenuItem>
              <MenuItem value={20}>20 / page</MenuItem>
            </Select>

            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeftIcon />
            </Button>

            <Button
              variant="contained"
              size="small"
              sx={{
                minWidth: 36
              }}
            >
              {page}
            </Button>

            <Button
              size="small"
              disabled={page >= totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default TicketTable;
